import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocalShipping as ShippingIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  BrokenImage as BrokenImageIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getMarketplaceItem,
  approveMarketplaceItem,
  rejectMarketplaceItem,
  removeMarketplaceItem,
  downloadMarketplaceDocumentBlob,
} from '../../endpoints/marketplace';
import AlertItem from '../../Components/AlertItem';
import {
  roles,
  applications,
  marketPlaceStatuses,
} from '../../utils/variables';
import { MarketplaceStatusChip } from '../../Components/Marketplace';

const MarketplaceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [documentDownloading, setDocumentDownloading] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);

  const {
    data: item,
    isLoading,
    error,
    refetch,
  } = useApiGet(['marketplace-item', id], () => getMarketplaceItem(id!), {
    enabled: !!id,
  });

  // Reset image error when item changes
  useEffect(() => {
    setImageError(false);
  }, [item]);

  // Sort images: primary first, then by display order
  const getSortedImages = () => {
    if (!item?.images) return [];
    return [...item.images].sort((a, b) => {
      // Primary image first
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      // Then by display order
      return (a.display_order || 0) - (b.display_order || 0);
    });
  };

  const sortedImages = getSortedImages();

  // Navigation functions
  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? sortedImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === sortedImages.length - 1 ? 0 : prev + 1
    );
  };

  // Reset current image index when item changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [item]);

  const canEdit = () => {
    const userRole = localStorage.getItem('role');
    const userApps = localStorage.getItem('apps');

    // Check if user is admin
    if (userRole === roles.Admin) {
      return true;
    }

    // Check if user has MarketplaceAdmin app (default to true for now as requested)
    if (userApps && userApps.includes(applications.MarketplaceAdmin)) {
      return true;
    }

    // Default to admin for now as requested
    return false;
  };

  const isAdmin = () => {
    const role = localStorage.getItem('role');
    return role === roles.Admin;
  };

  const handleApprove = async () => {
    if (!item) return;

    setIsProcessing(true);
    try {
      await approveMarketplaceItem(item.id);
      refetch();
    } catch (error) {
      console.error('Error approving item:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!item) return;

    setIsProcessing(true);
    try {
      await rejectMarketplaceItem(item.id, rejectionReason);
      refetch();
      setShowRejectDialog(false);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting item:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = async () => {
    if (!item) return;

    setIsProcessing(true);
    try {
      await removeMarketplaceItem(item.id);
      refetch();
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEdit = () => {
    if (!item) return;
    navigate(`/marketplace/${item.id}/edit`);
  };

  // Function to determine available actions based on status
  const getAvailableActions = () => {
    if (!item) return [];

    const actions = [];
    const statusId = item.status_id || item.status;

    switch (statusId) {
      case marketPlaceStatuses.AwaitingApproval:
        actions.push(
          {
            key: 'approve',
            label: 'Approve',
            color: 'success',
            icon: <CheckCircleIcon />,
            handler: handleApprove,
          },
          {
            key: 'reject',
            label: 'Reject',
            color: 'error',
            icon: <CancelIcon />,
            handler: () => setShowRejectDialog(true),
          }
        );
        break;
      case marketPlaceStatuses.Approved:
        actions.push({
          key: 'remove',
          label: 'Remove',
          color: 'warning',
          icon: <CancelIcon />,
          handler: handleRemove,
        });
        break;
      case marketPlaceStatuses.Rejected:
      case marketPlaceStatuses.Expired:
      case marketPlaceStatuses.Removed:
        actions.push({
          key: 'approve',
          label: 'Approve',
          color: 'success',
          icon: <CheckCircleIcon />,
          handler: handleApprove,
        });
        break;
      default:
        break;
    }

    return actions;
  };

  const formatPrice = (price: string) => {
    if (price.startsWith('$')) return price;
    // Check if price starts with a number
    if (/^\d/.test(price)) {
      return `$${price}`;
    }
    // For non-numeric prices like "Varies", "Free", etc., return as-is
    return price;
  };

  // Placeholder component for missing or broken images
  const ImagePlaceholder = ({ hasError = false }: { hasError?: boolean }) => (
    <Box
      sx={{
        width: '100%',
        height: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px 4px 0 0',
        border: '2px dashed #ccc',
        color: '#666',
      }}
    >
      {hasError ? (
        <BrokenImageIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
      ) : (
        <ImageIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
      )}
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {hasError ? 'Image Failed to Load' : 'No Image Available'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {hasError
          ? 'The image could not be displayed'
          : 'This item does not have an image'}
      </Typography>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading marketplace item"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/marketplace')}
        sx={{ mb: 2 }}
      >
        Back to Marketplace
      </Button>

      <Grid container spacing={3}>
        {/* Images */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ position: 'relative' }}>
              {!sortedImages || sortedImages.length === 0 ? (
                <ImagePlaceholder />
              ) : imageError ? (
                <ImagePlaceholder hasError />
              ) : (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: '300px', sm: '350px', md: '400px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px 4px 0 0',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={sortedImages[currentImageIndex]?.url}
                      alt={item.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        width: 'auto',
                        height: 'auto',
                      }}
                      onError={() => setImageError(true)}
                    />
                  </Box>

                  {/* Navigation arrows */}
                  {sortedImages.length > 1 && (
                    <>
                      <Button
                        onClick={handlePreviousImage}
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          minWidth: 'auto',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: 'text.primary',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <ChevronLeftIcon />
                      </Button>
                      <Button
                        onClick={handleNextImage}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          minWidth: 'auto',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: 'text.primary',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <ChevronRightIcon />
                      </Button>
                    </>
                  )}

                  {/* Image counter */}
                  {sortedImages.length > 1 && (
                    <Chip
                      label={`${currentImageIndex + 1} / ${sortedImages.length}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: 'text.primary',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        },
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Item Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <Typography variant="h4" component="h1">
                  {item.name}
                </Typography>
                <MarketplaceStatusChip status={item.status} />
              </Box>

              <Typography
                variant="h5"
                color="primary"
                sx={{ mb: 3, fontWeight: 'bold' }}
              >
                {formatPrice(item.price)}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {item.description}
              </Typography>

              {/* Link URL */}
              {item.link_url && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    href={item.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: '#0072CE',
                      '&:hover': {
                        backgroundColor: '#005a9e',
                      },
                    }}
                  >
                    View Item Details
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Category and Tags - Only show for users who can edit */}
              {canEdit() && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Category
                    </Typography>
                    <Chip label={item.category} size="small" sx={{ mr: 1 }} />
                    {item.subcategory && (
                      <Chip
                        label={item.subcategory}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {item.tags && item.tags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Tags
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {item.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}

              {/* Order Instructions */}
              {item.order_instructions && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <DescriptionIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: 'middle' }}
                    />
                    Order Instructions
                  </Typography>
                  <Typography variant="body2">
                    {item.order_instructions}
                  </Typography>
                </Box>
              )}

              {/* Shipping Information */}
              {item.shipping_details && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <ShippingIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: 'middle' }}
                    />
                    Shipping Information
                  </Typography>
                  <Typography variant="body2">
                    {item.shipping_details}
                  </Typography>
                </Box>
              )}

              {/* Document */}
              {item.document && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <AttachFileIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: 'middle' }}
                    />
                    Document
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    disabled={!item?.document?.url || documentDownloading}
                    onClick={async () => {
                      if (!item?.document?.url) return;
                      setDocumentError(null);
                      setDocumentDownloading(true);
                      try {
                        const { blob, filename } =
                          await downloadMarketplaceDocumentBlob(
                            item.document!.url
                          );
                        const objectUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = objectUrl;
                        a.download =
                          filename || item.document?.name || 'document';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(objectUrl);
                      } catch (err: unknown) {
                        const message =
                          (
                            err as {
                              response?: { data?: { error?: string } };
                              message?: string;
                            }
                          )?.response?.data?.error ||
                          (err as Error)?.message ||
                          'Failed to download document';
                        setDocumentError(message);
                      } finally {
                        setDocumentDownloading(false);
                      }
                    }}
                    sx={{
                      color: '#0072CE',
                      borderColor: '#0072CE',
                      '&:hover': {
                        borderColor: '#005a9e',
                        backgroundColor: 'rgba(0, 114, 206, 0.04)',
                      },
                    }}
                  >
                    {documentDownloading ? 'Downloading…' : 'Download Document'}
                  </Button>
                  {documentError && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      {documentError}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Contact Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Contact Information
                </Typography>
                <Stack spacing={1}>
                  {/* Always show chapter */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationIcon
                      color="action"
                      fontSize="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body2">
                      {item.local_chapter}
                    </Typography>
                  </Box>

                  {/* Conditionally show contact name */}
                  {item.show_contact_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon
                        color="action"
                        fontSize="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        {item.contact_name}
                      </Typography>
                    </Box>
                  )}

                  {/* Conditionally show email */}
                  {item.show_contact_email && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon
                        color="action"
                        fontSize="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        {item.contact_email}
                      </Typography>
                    </Box>
                  )}

                  {/* Conditionally show phone */}
                  {item.show_contact_phone && item.contact_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon
                        color="action"
                        fontSize="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        {item.contact_phone}
                      </Typography>
                    </Box>
                  )}

                  {/* Conditionally show address information */}
                  {item.show_contact_address && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Address
                      </Typography>
                      <Stack spacing={1}>
                        {item.contact_address_1 && (
                          <Typography variant="body2">
                            {item.contact_address_1}
                          </Typography>
                        )}
                        {item.contact_address_2 && (
                          <Typography variant="body2">
                            {item.contact_address_2}
                          </Typography>
                        )}
                        {(item.contact_city ||
                          item.contact_state ||
                          item.contact_postal_code) && (
                          <Typography variant="body2">
                            {[
                              item.contact_city,
                              item.contact_state,
                              item.contact_postal_code,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        )}
                        {item.contact_country && (
                          <Typography variant="body2">
                            {item.contact_country}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                {canEdit() && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    sx={{
                      backgroundColor: '#0072CE',
                      '&:hover': {
                        backgroundColor: '#005a9e',
                      },
                    }}
                  >
                    Edit Item
                  </Button>
                )}

                {/* Status Management Actions */}
                {isAdmin() && getAvailableActions().length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {getAvailableActions().map((action) => (
                      <Button
                        key={action.key}
                        variant="contained"
                        color={action.color as any}
                        startIcon={action.icon}
                        onClick={action.handler}
                        disabled={isProcessing}
                        size="small"
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reject Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Item</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for rejection"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={isProcessing || !rejectionReason.trim()}
          >
            Reject Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplaceDetail;
